import { requireActiveOrg } from '../lib/config'
import { FlightDeskAPI } from '../lib/api'
import { execSync, spawn, spawnSync } from 'node:child_process'
import * as path from 'node:path'
import * as os from 'node:os'
import * as fs from 'node:fs'

interface PreviewStatusOptions {
  taskId: string
}

/**
 * Validate that a container ID is safe (hex characters only)
 * Docker container IDs are 64-char hex strings (or 12-char truncated)
 */
function isValidContainerId(id: string): boolean {
  return /^[a-fA-F0-9]+$/.test(id) && id.length >= 12 && id.length <= 64
}

/**
 * Validate SSH connection parameters to prevent injection
 */
function validateSSHParams(instance: { sshUser: string; sshHost: string; sshPort: number; containerId?: string }): void {
  // Validate container ID if present (hex only)
  if (instance.containerId && !isValidContainerId(instance.containerId)) {
    throw new Error(`Invalid container ID format: ${instance.containerId}`)
  }
  // Validate SSH user (alphanumeric, underscore, hyphen)
  if (!/^[a-zA-Z0-9_-]+$/.test(instance.sshUser)) {
    throw new Error(`Invalid SSH user format: ${instance.sshUser}`)
  }
  // Validate SSH host (alphanumeric, dots, hyphens for hostname/IP)
  if (!/^[a-zA-Z0-9.-]+$/.test(instance.sshHost)) {
    throw new Error(`Invalid SSH host format: ${instance.sshHost}`)
  }
  // Validate port is a reasonable number
  if (!Number.isInteger(instance.sshPort) || instance.sshPort < 1 || instance.sshPort > 65535) {
    throw new Error(`Invalid SSH port: ${instance.sshPort}`)
  }
}

interface PreviewLogsOptions {
  taskId: string
  lines?: number
  follow?: boolean
}

interface PreviewMountOptions {
  taskId: string
  directory?: string
}

interface PreviewUnmountOptions {
  taskId: string
}

interface PreviewRestartOptions {
  taskId: string
}

interface PreviewResumeOptions {
  taskId: string
}

interface PreviewTeardownOptions {
  taskId: string
}

type PreviewOptions =
  | PreviewStatusOptions
  | PreviewLogsOptions
  | PreviewMountOptions
  | PreviewUnmountOptions
  | PreviewRestartOptions
  | PreviewResumeOptions
  | PreviewTeardownOptions

/**
 * Preview environment management commands
 */
export async function previewCommand(
  action: 'status' | 'logs' | 'mount' | 'unmount' | 'restart' | 'resume' | 'teardown',
  options: PreviewOptions
): Promise<void> {
  const { config, org } = requireActiveOrg()
  const api = FlightDeskAPI.fromConfig(config, org)

  try {
    switch (action) {
      case 'status':
        await handleStatus(api, options as PreviewStatusOptions)
        break
      case 'logs':
        await handleLogs(api, options as PreviewLogsOptions)
        break
      case 'mount':
        await handleMount(api, options as PreviewMountOptions)
        break
      case 'unmount':
        await handleUnmount(api, options as PreviewUnmountOptions)
        break
      case 'restart':
        await handleRestart(api, options as PreviewRestartOptions)
        break
      case 'resume':
        await handleResume(api, options as PreviewResumeOptions)
        break
      case 'teardown':
        await handleTeardown(api, options as PreviewTeardownOptions)
        break
    }
  } catch (error) {
    console.error(`Error: ${error}`)
    process.exit(1)
  }
}

function getStatusEmoji(status: string): string {
  const statusEmojis: Record<string, string> = {
    STARTING: '🔄',
    READY: '✅',
    SUSPENDED: '💤',
    RESTARTING: '🔄',
    TEARDOWN: '🗑️',
    ERROR: '❌',
  }
  return statusEmojis[status] || '❓'
}

async function handleStatus(api: FlightDeskAPI, options: PreviewStatusOptions): Promise<void> {
  const instance = await api.getTaskInstance(options.taskId)

  if (!instance) {
    console.log('\n⚠️  No preview environment found for this task')
    console.log('   The preview environment may not have been created yet,')
    console.log('   or the PR may be closed.')
    return
  }

  const emoji = getStatusEmoji(instance.status)

  console.log('\n📦 Preview Environment')
  console.log('─'.repeat(50))
  console.log(`${emoji} Status: ${instance.status}`)
  console.log(`   ID: ${instance.id.substring(0, 8)}`)

  // Show process URLs if available, otherwise single preview URL
  if (instance.processUrls && instance.processUrls.length > 0) {
    console.log('   URLs:')
    for (const pu of instance.processUrls) {
      const label = pu.primary ? `${pu.name} (primary)` : pu.name
      console.log(`     ${label}: ${pu.url}`)
    }
  } else {
    console.log(`   Preview URL: ${instance.previewUrl}`)
  }

  console.log(`   SSH: ${instance.sshConnectionString}`)
  console.log(`   Container: ${instance.containerId.substring(0, 12)}`)

  if (instance.lastActivityAt) {
    const lastActivity = new Date(instance.lastActivityAt)
    const minutesAgo = Math.round((Date.now() - lastActivity.getTime()) / 60000)
    console.log(`   Last Activity: ${minutesAgo} minutes ago`)
  }

  if (instance.suspendedAt) {
    const suspended = new Date(instance.suspendedAt)
    console.log(`   Suspended At: ${suspended.toLocaleString()}`)
  }

  console.log(`   Created: ${new Date(instance.createdAt).toLocaleString()}`)
  console.log('')
}

async function handleLogs(api: FlightDeskAPI, options: PreviewLogsOptions): Promise<void> {
  const lines = options.lines || 100

  if (options.follow) {
    // For follow mode, we need the SSH details to tail logs directly
    const instance = await api.getTaskInstance(options.taskId)

    if (!instance) {
      console.error('No preview environment found for this task')
      process.exit(1)
    }

    console.log(`📋 Streaming logs from ${instance.previewUrl}...\n`)
    console.log(`(Press Ctrl+C to stop)\n`)

    // Validate SSH parameters to prevent command injection
    validateSSHParams(instance)

    // Use SSH to tail the logs (containerId validated above)
    // NOSONAR: PATH-based lookup is standard for CLI tools; user controls their own environment
    const sshCommand = `docker logs -f ${instance.containerId}`
    const ssh = spawn('ssh', [
      '-o', 'StrictHostKeyChecking=no',
      '-o', 'UserKnownHostsFile=/dev/null',
      '-p', String(instance.sshPort),
      `${instance.sshUser}@${instance.sshHost}`,
      sshCommand,
    ])

    ssh.stdout.pipe(process.stdout)
    ssh.stderr.pipe(process.stderr)

    ssh.on('close', (code) => {
      process.exit(code || 0)
    })

    // Handle Ctrl+C
    process.on('SIGINT', () => {
      ssh.kill()
      process.exit(0)
    })

    return
  }

  // Non-follow mode: fetch logs via API
  const result = await api.getInstanceLogs(options.taskId, lines)

  if (!result.logs) {
    console.log('No logs available')
    return
  }

  console.log(result.logs)
}

async function handleMount(api: FlightDeskAPI, options: PreviewMountOptions): Promise<void> {
  const instance = await api.getTaskInstance(options.taskId)

  if (!instance) {
    console.error('No preview environment found for this task')
    process.exit(1)
  }

  if (instance.status === 'SUSPENDED') {
    console.log('⏸️  Instance is suspended. Resuming...')
    await api.resumeInstance(options.taskId)
    // Wait a moment for resume
    await new Promise((resolve) => setTimeout(resolve, 3000))
  }

  // Check if sshfs is installed
  // NOSONAR: PATH-based lookup is standard for CLI tools; user controls their own environment
  try {
    execSync('which sshfs', { stdio: 'ignore' })
  } catch {
    console.error('❌ sshfs is not installed.')
    console.error('')
    console.error('Install it with:')
    if (process.platform === 'darwin') {
      console.error('  brew install macfuse gromgit/fuse/sshfs-mac')
    } else if (process.platform === 'linux') {
      console.error('  sudo apt install sshfs')
    } else {
      console.error('  SSHFS is not supported on this platform')
    }
    process.exit(1)
  }

  // Sanitize taskId to prevent path traversal attacks
  const rawTaskIdPrefix = options.taskId.substring(0, 8)
  const safeTaskId = path.basename(rawTaskIdPrefix).replaceAll(/[^a-zA-Z0-9_-]/g, '') || 'task'
  const mountDir = options.directory || path.join(os.homedir(), 'flightdesk-mounts', safeTaskId)

  // Create mount directory if it doesn't exist
  if (!fs.existsSync(mountDir)) {
    fs.mkdirSync(mountDir, { recursive: true })
  }

  // Check if already mounted
  // NOSONAR: PATH-based lookup is standard for CLI tools; user controls their own environment
  try {
    const mounted = execSync('mount', { encoding: 'utf8' })
    if (mounted.includes(mountDir)) {
      console.log(`📁 Already mounted at ${mountDir}`)
      return
    }
  } catch {
    // Ignore mount check errors
  }

  console.log(`📁 Mounting preview environment to ${mountDir}...`)

  // Validate SSH parameters to prevent injection
  validateSSHParams(instance)

  // Use spawnSync with arguments array to avoid shell injection
  // NOSONAR: PATH-based lookup is standard for CLI tools; user controls their own environment
  const sshfsArgs = [
    '-o', 'StrictHostKeyChecking=no',
    '-o', 'UserKnownHostsFile=/dev/null',
    '-o', 'reconnect',
    '-o', 'ServerAliveInterval=15',
    '-o', 'ServerAliveCountMax=3',
    '-p', String(instance.sshPort),
    `${instance.sshUser}@${instance.sshHost}:/app`,
    mountDir,
  ]

  try {
    const result = spawnSync('sshfs', sshfsArgs, { stdio: 'inherit' })
    if (result.status !== 0) {
      throw new Error(`sshfs exited with code ${result.status}`)
    }
    console.log('')
    console.log('✅ Mounted successfully!')
    console.log(`   Location: ${mountDir}`)
    console.log('')
    console.log('   To unmount:')
    console.log(`   fd preview unmount ${options.taskId}`)
    console.log('')
    console.log('   Or manually:')
    if (process.platform === 'darwin') {
      console.log(`   umount ${mountDir}`)
    } else {
      console.log(`   fusermount -u ${mountDir}`)
    }
  } catch (error) {
    console.error(`❌ Mount failed: ${error}`)
    process.exit(1)
  }
}

async function handleUnmount(_api: FlightDeskAPI, options: PreviewUnmountOptions): Promise<void> {
  // Sanitize taskId to prevent path traversal
  const rawTaskIdPrefix = options.taskId.substring(0, 8)
  const safeTaskId = path.basename(rawTaskIdPrefix).replaceAll(/[^a-zA-Z0-9_-]/g, '') || 'task'
  const mountDir = path.join(os.homedir(), 'flightdesk-mounts', safeTaskId)

  if (!fs.existsSync(mountDir)) {
    console.log('Mount directory does not exist')
    return
  }

  console.log(`📁 Unmounting ${mountDir}...`)

  try {
    // Use spawnSync with arguments array to avoid shell injection
    // NOSONAR: PATH-based lookup is standard for CLI tools; user controls their own environment
    let result
    if (process.platform === 'darwin') {
      result = spawnSync('umount', [mountDir], { stdio: 'inherit' })
    } else {
      result = spawnSync('fusermount', ['-u', mountDir], { stdio: 'inherit' })
    }
    if (result.status !== 0) {
      throw new Error(`Unmount exited with code ${result.status}`)
    }
    console.log('✅ Unmounted successfully')

    // Try to remove the directory (and any nested contents)
    try {
      fs.rmSync(mountDir, { recursive: true, force: true })
    } catch {
      // Directory cleanup failure is non-fatal
    }
  } catch (error) {
    console.error(`❌ Unmount failed: ${error}`)
    console.error('')
    console.error('Try force unmounting:')
    if (process.platform === 'darwin') {
      console.error(`   diskutil unmount force ${mountDir}`)
    } else {
      console.error(`   fusermount -uz ${mountDir}`)
    }
    process.exit(1)
  }
}

async function handleRestart(api: FlightDeskAPI, options: PreviewRestartOptions): Promise<void> {
  console.log('🔄 Restarting preview environment...')

  await api.restartInstance(options.taskId)

  console.log('✅ Restart initiated. The preview will be available shortly.')
}

async function handleResume(api: FlightDeskAPI, options: PreviewResumeOptions): Promise<void> {
  console.log('▶️  Resuming preview environment...')

  await api.resumeInstance(options.taskId)

  console.log('✅ Resume initiated. The preview should be ready in about 30 seconds.')
}

async function handleTeardown(api: FlightDeskAPI, options: PreviewTeardownOptions): Promise<void> {
  console.log('🗑️  Tearing down preview environment...')

  await api.tearDownInstance(options.taskId)

  console.log('✅ Preview environment has been torn down.')
}
